import {
  EnhancedSearchResponse,
  GetComponentNavigationOptions,
  sortableColumnTypes,
} from './types';
import { showToast, ToastType } from '@ardoq/status-ui';
import {
  APIComponentAttributes,
  APIEntityType,
  APIFieldType,
  APIReferenceAttributes,
  APIReportFilters,
  APISearchResponse,
  ArdoqId,
  DataSourceType,
  DateRangeFieldDefaultValue,
  Operator,
  ReportColumn,
  ReportColumnType,
} from '@ardoq/api-types';
import { isJust, isTruthy, Maybe } from '@ardoq/common-helpers';
import {
  Checkbox,
  Date,
  DEFAULT_MAX_TEXT_LENGTH,
  Empty,
  EnhancedScopeDataWithBranchName,
  NoValue,
  DateRange,
} from '@ardoq/renderers';
import {
  dateRangeOperations,
  getDateRangePartType,
  getDateRangeType,
} from '@ardoq/date-range';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { isISO8601DateOnly, isISO8601DateTime } from '@ardoq/date-time';
import { FILTERS_URL_KEY, REFERRER_URL_KEY } from './consts';
import { LinkDecorator } from '@ardoq/decorators';
import type { DataSourceLabels } from './labels';

export const isEmptyOptionSelected = (filterOption?: Operator) =>
  filterOption === Operator.IS_EMPTY || filterOption === Operator.IS_NOT_EMPTY;

export const isBetweenOptionSelected = (filterOption?: Operator) =>
  filterOption === Operator.BETWEEN_INCLUSIVE;

export const isAPIFieldTypeSortable = (dataType: APIFieldType) =>
  sortableColumnTypes.includes(dataType);

export const isColumnSortAble = (column: ReportColumn) => {
  if (
    (column.type === ReportColumnType.CUSTOM ||
      column.type === ReportColumnType.FIELD) &&
    column.dataType
  ) {
    return isAPIFieldTypeSortable(column.dataType);
  }
  return false;
};

export const getDataSourceLabel = (
  datasource: DataSourceType,
  labels?: DataSourceLabels
): string | undefined => {
  switch (datasource) {
    case DataSourceType.ADVANCED_SEARCH:
      return labels?.advancedSearch ?? 'Advanced search';
    case DataSourceType.GREMLIN_SEARCH:
      return labels?.gremlinSearch ?? 'Gremlin graph search';
    default:
      return undefined;
  }
};

export const enrichColumnsWithNumberFormatOptions = (
  columns: ReportColumn[],
  scopeData: EnhancedSearchResponse['scopeData']
): ReportColumn[] => {
  if (!scopeData) {
    return columns;
  }

  return columns.map(column => {
    if (column.numberFormatOptions !== undefined) {
      return column;
    }
    const field = scopeData.fields.find(f => f.name === column.key);
    if (field && 'numberFormatOptions' in field) {
      return {
        ...column,
        numberFormatOptions: field.numberFormatOptions ?? null,
      };
    }
    return column;
  });
};

export const mergeDateRangeFieldsInColumns = (
  columns: ReportColumn[],
  isDateRangeColumn: (column: ReportColumn) => boolean
) => {
  const dateRangePairs = columns.reduce<
    Record<string, { start?: ReportColumn; end?: ReportColumn }>
  >((pairs, column) => {
    if (isDateRangeColumn(column)) {
      const dateRangeName = dateRangeOperations.extractDateRangeFieldName(
        column.key
      );

      pairs[dateRangeName] = {
        ...pairs[dateRangeName],
        [dateRangeOperations.fieldNameIsDateRangeStartField(column.key)
          ? 'start'
          : 'end']: column,
      };

      return pairs;
    }
    return pairs;
  }, {});

  return columns
    .map(column => {
      if (!isDateRangeColumn(column)) {
        return column;
      }
      const dateRangeFieldName = dateRangeOperations.extractDateRangeFieldName(
        column.key
      );
      if (
        dateRangePairs[dateRangeFieldName].start &&
        dateRangePairs[dateRangeFieldName].end
      ) {
        if (dateRangeOperations.fieldNameIsDateRangeStartField(column.key)) {
          let dateRangeColumn = {
            ...column,
            key: dateRangeFieldName,
            label: dateRangeOperations.extractDateRangeFieldLabel(column.label),
            ...(column.dataType
              ? { dataType: getDateRangeType(column.dataType) }
              : {}),
            isDateRangeColumn: true,
          };
          if (column.originalLabel) {
            dateRangeColumn = {
              ...dateRangeColumn,
              originalLabel: dateRangeOperations.extractDateRangeFieldLabel(
                column.originalLabel
              ),
            };
          }
          return dateRangeColumn;
        }
        return null;
      }
      return column;
    })
    .filter(isTruthy);
};

export const splitDateRangeColumn = (
  column: ReportColumn
): ReportColumn | ReportColumn[] => {
  if (!column.isDateRangeColumn) return column;

  const originalLabel = column.originalLabel;
  const partDataType = column.dataType
    ? getDateRangePartType(column.dataType)
    : column.dataType;
  return [
    {
      type: column.type,
      dataType: partDataType,
      sort: column.sort,
      numberFormatOptions: column.numberFormatOptions,
      key: dateRangeOperations.toStartDateName(column.key),
      label: dateRangeOperations.toStartDateLabel(column.label),
      ...(originalLabel
        ? { originalLabel: dateRangeOperations.toStartDateLabel(originalLabel) }
        : {}),
    },
    {
      type: column.type,
      dataType: partDataType,
      sort: column.sort,
      numberFormatOptions: column.numberFormatOptions,
      key: dateRangeOperations.toEndDateName(column.key),
      label: dateRangeOperations.toEndDateLabel(column.label),
      ...(originalLabel
        ? { originalLabel: dateRangeOperations.toEndDateLabel(originalLabel) }
        : {}),
    },
  ];
};

export const splitDateRangeColumnForUI = (
  column: ReportColumn
): ReportColumn | ReportColumn[] => {
  if (!column.isDateRangeColumn) return column;

  const originalLabel = column.originalLabel;
  const partDataType = column.dataType
    ? getDateRangePartType(column.dataType)
    : column.dataType;
  return [
    {
      type: column.type,
      dataType: partDataType,
      sort: column.sort,
      numberFormatOptions: column.numberFormatOptions,
      key: dateRangeOperations.toStartDateName(column.key),
      label: `${column.label} - Start`,
      ...(originalLabel
        ? { originalLabel: dateRangeOperations.toStartDateLabel(originalLabel) }
        : {}),
    },
    {
      type: column.type,
      dataType: partDataType,
      sort: column.sort,
      numberFormatOptions: column.numberFormatOptions,
      key: dateRangeOperations.toEndDateName(column.key),
      label: `${column.label} - End`,
      ...(originalLabel
        ? { originalLabel: dateRangeOperations.toEndDateLabel(originalLabel) }
        : {}),
    },
  ];
};

export const isDateRangeReportColumn = (column: ReportColumn): boolean =>
  dateRangeOperations.fieldNameIsPartOfDateRangeField(column.key) &&
  dateRangeOperations.fieldLabelIsPartOfDateRangeField(column.label) &&
  (column.dataType === APIFieldType.DATE_TIME ||
    column.dataType === APIFieldType.DATE_ONLY);

export const mergeDateRangeFieldsInResponseWithoutScopeData = (
  response: APISearchResponse
): EnhancedSearchResponse => {
  const columnsWithMergedDateRanges = mergeDateRangeFieldsInColumns(
    response.columns,
    isDateRangeReportColumn
  );

  const goneColumnsWithMergedDateRanges = mergeDateRangeFieldsInColumns(
    response.goneColumns ?? [],
    isDateRangeReportColumn
  );

  const columnKeys = response.columns.map(col => col.key);
  const dateRangePairs =
    dateRangeOperations.getDateRangeFieldNamePairsFromFieldNames(columnKeys);
  const resultsWithMergedDateRanges = response.results.map(entity => {
    return dateRangeOperations.mergeDateRangeFieldsByFieldPairs(
      entity,
      dateRangePairs
    );
  });
  return {
    ...response,
    scopeData: null,
    columns: columnsWithMergedDateRanges,
    goneColumns: goneColumnsWithMergedDateRanges,
    results: resultsWithMergedDateRanges,
  };
};

export const mergeDateRangeFieldsInResponse = (
  response: EnhancedSearchResponse
): EnhancedSearchResponse => {
  const scopeDataWithDateRangeFields =
    response.scopeData &&
    dateRangeOperations.getMergedDateRangeFieldForScopeData(response.scopeData);

  const isDateRangeColumn = (column: ReportColumn) => {
    if (!dateRangeOperations.fieldNameIsPartOfDateRangeField(column.key))
      return false;
    const dateRangeFieldName = dateRangeOperations.extractDateRangeFieldName(
      column.key
    );
    const dateRangeFields = Array.from(
      // dateRangeFieldMap is map with this kind of structure: Map([workspaceId, {dateRangeFieldName: {start: APIFieldAttributes, end: APIFieldAttributes}, ...}, ...] )
      scopeDataWithDateRangeFields?.dateRangeFieldMap.values() ?? []
    ).reduce(
      (acc, dateRangeFieldsObject) =>
        Object.entries(dateRangeFieldsObject).reduce(
          (acc, [fieldName, { start, end }]) => {
            acc[fieldName] = { ...acc[fieldName], start, end };

            return acc;
          },
          acc
        ),
      {}
    );

    return Boolean(
      dateRangeFields[dateRangeFieldName]?.start &&
      dateRangeFields[dateRangeFieldName]?.end
    );
  };

  const columnsWithMergedDateRanges = mergeDateRangeFieldsInColumns(
    response.columns,
    isDateRangeColumn
  );

  const goneColumnsWithMergedDateRanges = mergeDateRangeFieldsInColumns(
    response.goneColumns ?? [],
    isDateRangeColumn
  );

  const resultsWithMergedDateRanges = scopeDataWithDateRangeFields
    ? (
        response.results as Array<
          APIComponentAttributes | APIReferenceAttributes
        >
      ).map(entity =>
        dateRangeOperations.mergeDateRangeFieldsForEntity(
          entity,
          scopeDataWithDateRangeFields.dateRangeFieldMap
        )
      )
    : response.results;
  return {
    ...response,
    scopeData: scopeDataWithDateRangeFields,
    columns: columnsWithMergedDateRanges,
    goneColumns: goneColumnsWithMergedDateRanges,
    results: resultsWithMergedDateRanges,
  };
};

export const isReferenceRow = (ardoq?: { 'entity-type': APIEntityType }) =>
  Boolean(ardoq?.['entity-type'] === APIEntityType.REFERENCE);
export const isComponentRow = (ardoq?: { 'entity-type': APIEntityType }) =>
  Boolean(ardoq?.['entity-type'] === APIEntityType.COMPONENT);

const isSomething = (rawValue: unknown) =>
  typeof rawValue !== 'undefined' && rawValue !== null;

const isPrimitive = (rawValue: unknown) =>
  isSomething(rawValue) &&
  !(typeof rawValue === 'object' || typeof rawValue === 'function');

const isDateString = (rawValue: string) =>
  isISO8601DateTime(rawValue) || isISO8601DateOnly(rawValue);

const isDateRange = (
  rawValue: unknown
): rawValue is DateRangeFieldDefaultValue => {
  return (
    isSomething(rawValue) &&
    typeof rawValue === 'object' &&
    Object.keys(rawValue).length === 2 &&
    'start' in rawValue &&
    'end' in rawValue &&
    (rawValue.start === null ||
      (typeof rawValue.start === 'string' && isDateString(rawValue.start))) &&
    (rawValue.end === null ||
      (typeof rawValue.end === 'string' && isDateString(rawValue.end)))
  );
};

export const renderRawColumnValue = (
  rawValue: unknown,
  dataType?: APIFieldType | null,
  underlineOnHover?: boolean
) => {
  if (dataType === APIFieldType.CHECKBOX) {
    return <Checkbox value={rawValue} />;
  } else if (typeof rawValue === 'boolean') {
    return rawValue ? 'True' : 'False';
  } else if (Array.isArray(rawValue) && rawValue.find(isPrimitive)) {
    const value = rawValue.join(', ');
    // Using "key" here makes sure that a new popover is made when the textOverflow changes position in the DOM, e.g. when sorting a table
    return <TruncatedTextPopover key={value}>{value}</TruncatedTextPopover>;
  } else if (Array.isArray(rawValue) && !rawValue.length) {
    return <Empty />;
  } else if (isDateRange(rawValue)) {
    return <DateRange value={rawValue} />;
  } else if (isSomething(rawValue) && !isPrimitive(rawValue)) {
    const value = JSON.stringify(rawValue);
    return <TruncatedTextPopover key={value}>{value}</TruncatedTextPopover>;
  } else if (rawValue === null || typeof rawValue === 'undefined') {
    return <NoValue />;
  } else if (typeof rawValue === 'string') {
    if (isDateString(rawValue)) {
      return <Date key={rawValue} value={rawValue} />;
    }
    if (dataType === APIFieldType.URL) {
      return (
        <LinkDecorator
          maxWidth={DEFAULT_MAX_TEXT_LENGTH}
          underlineOnHover={underlineOnHover}
          href={rawValue}
          target="_blank"
          rel="noopener noreferrer"
        />
      );
    }
    return (
      <TruncatedTextPopover key={rawValue}>{rawValue}</TruncatedTextPopover>
    );
  }
  return rawValue;
};

export const copyReportInArdoqURL = (reportId: ArdoqId, stateId?: ArdoqId) => {
  const stateString = stateId ? `?state=${stateId}` : '';
  navigator.clipboard.writeText(
    `${window.location.origin}/app/report/reader/${reportId}${stateString}`
  );
  showToast('Link copied.', ToastType.SUCCESS);
};

export const copyReportInDiscoverURL = (reportId: ArdoqId) => {
  navigator.clipboard.writeText(
    `${window.location.origin}/discover/report/reader/${reportId}`
  );
  showToast('Link copied.', ToastType.SUCCESS);
};

export const getReportInDiscoverURL = (
  reportId: ArdoqId,
  filters?: APIReportFilters,
  referrer?: string
) => {
  const baseUrl = `${window.location.origin}/discover/report/reader/${reportId}`;
  if (!filters && !referrer) return baseUrl;
  const query = new URLSearchParams();

  if (filters) {
    query.append(FILTERS_URL_KEY, JSON.stringify(filters));
  }
  if (referrer) {
    query.append(REFERRER_URL_KEY, referrer);
  }
  return `${baseUrl}?${query.toString()}`;
};

export const getNavigationOptionsForTargetComponent = ({
  enhancedScopeData,
  targetId,
  getComponentNavigationOptions,
}: {
  targetId: ArdoqId;
  enhancedScopeData: EnhancedScopeDataWithBranchName;
  getComponentNavigationOptions?: GetComponentNavigationOptions;
}) => {
  if (!getComponentNavigationOptions || !isJust(targetId)) return [];

  const targetComponent = enhancedScopeDataOperations.getComponent(
    enhancedScopeData,
    targetId
  );

  return targetComponent ? getComponentNavigationOptions(targetComponent) : [];
};

export const isReferenceColumn = (
  column: ReportColumn
): column is ReportColumn & {
  type:
    | ReportColumnType.REFERENCE_TYPE_INCOMING
    | ReportColumnType.REFERENCE_TYPE_OUTGOING;
} =>
  column.type === ReportColumnType.REFERENCE_TYPE_INCOMING ||
  column.type === ReportColumnType.REFERENCE_TYPE_OUTGOING;

const isTypeColumn = (column: ReportColumn): boolean =>
  column.key === 'type' || column.key === 'typeId';

// Columns that are known to always have a value
export const isNonEmptyColumn = (column: ReportColumn): boolean =>
  isTypeColumn(column) || column.key === 'name';

export const isUserColumn = (
  column: ReportColumn
): column is ReportColumn & { dataType: APIFieldType.USER } =>
  column.type === ReportColumnType.FIELD &&
  column.dataType === APIFieldType.USER;

export const getColumnOriginalLabel = (column: ReportColumn) => {
  return column.originalLabel || column.label;
};

export const getColumnCustomLabel = (column: ReportColumn): Maybe<string> => {
  // column.originalLabel === column.label only happens when resetting name
  if (column.originalLabel && column.originalLabel !== column.label) {
    return column.label;
  }
  return null;
};

export const isColumnWithCustomLabel = (
  column: ReportColumn
): column is ReportColumn & { originalLabel: string } => {
  return Boolean(getColumnCustomLabel(column));
};

export const toSortKeyArg = (
  columns: ReportColumn[] | undefined,
  columnKey: Maybe<string>
): Maybe<string> => {
  if (!columns || !columnKey) return columnKey;

  const sortedColumn = columns.find(column => column.key === columnKey);
  return sortedColumn?.isDateRangeColumn
    ? dateRangeOperations.toStartDateName(sortedColumn.key)
    : columnKey;
};
